<template>
  <!-- Plan-coloured summary box (profile menu + profile sidebar). `info` is the
       /api/subscription/status payload; action buttons come in via the slot. -->
  <div class="plan-card" :class="'plan-card--' + plan">
    <span v-if="label" class="plan-card__label">{{ label }}</span>
    <div class="plan-card__row">
      <span class="plan-card__name">{{ planName }}</span>
      <span v-if="postsLimit">{{ postsUsed }} of {{ postsLimit }} posts</span>
      <span v-else>Unlimited posts</span>
    </div>
    <div v-if="postsLimit" class="plan-card__meter" role="progressbar"
      :aria-valuenow="postsUsed" aria-valuemin="0" :aria-valuemax="postsLimit"
      :aria-label="`${postsUsed} of ${postsLimit} posts used this month`">
      <i :style="{ width: pct(postsUsed, postsLimit) + '%' }"></i>
    </div>

    <template v-if="showFeatured">
      <div class="plan-card__row plan-card__row--sub">
        <span>Featured</span>
        <span>{{ featuredUsed }} of {{ featuredLimit }} this month</span>
      </div>
      <div class="plan-card__meter" role="progressbar"
        :aria-valuenow="featuredUsed" aria-valuemin="0" :aria-valuemax="featuredLimit"
        :aria-label="`${featuredUsed} of ${featuredLimit} featured uses this month`">
        <i :style="{ width: pct(featuredUsed, featuredLimit) + '%' }"></i>
      </div>
    </template>

    <p class="plan-card__note">{{ expiryNote }}</p>
    <p v-if="detailed && info.imagesPerPost" class="plan-card__note">{{ info.imagesPerPost }} images per post</p>
    <p v-if="info.cancelAtPeriodEnd && periodEnd" class="plan-card__note plan-card__note--warn">
      Ends {{ periodEnd }} · then switches to Free
    </p>
    <p v-else-if="periodEnd" class="plan-card__note">Renews {{ periodEnd }}</p>

    <slot />
  </div>
</template>

<script>
const PLAN_NAMES = { free: "Free plan", premium: "Premium plan", business: "Business plan" };
const EXPIRY_NOTES = {
  free: "Ads expire after 30 days",
  premium: "Ads auto-renew while subscribed",
  business: "Ads never expire while subscribed",
};

export default {
  name: "PlanSummaryCard",
  props: {
    info: { type: Object, required: true },
    label: { type: String, default: "" },
    // Sidebar shows a little more (images per post)
    detailed: { type: Boolean, default: false },
  },
  computed: {
    plan() {
      return PLAN_NAMES[this.info.plan] ? this.info.plan : "free";
    },
    planName() {
      return PLAN_NAMES[this.plan];
    },
    expiryNote() {
      return EXPIRY_NOTES[this.plan];
    },
    postsUsed() {
      return Number(this.info.postsUsed) || 0;
    },
    // null = unlimited (API sends "Unlimited")
    postsLimit() {
      return typeof this.info.postsLimit === "number" ? this.info.postsLimit : null;
    },
    featuredUsed() {
      return Number(this.info.featuredUsed) || 0;
    },
    featuredLimit() {
      return Number(this.info.featuredLimit) || 0;
    },
    // Only advertise featured uses when featured placement is actually live
    showFeatured() {
      return this.info.featuredPlacement === true && this.featuredLimit > 0;
    },
    // Hidden when missing or already past (e.g. admin-granted plan with a stale
    // Stripe period) — "Renews <last May>" would be wrong.
    periodEnd() {
      const end = this.info.currentPeriodEnd ? new Date(this.info.currentPeriodEnd) : null;
      if (!end || isNaN(end) || end < new Date()) return "";
      return end.toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      });
    },
  },
  methods: {
    pct(used, limit) {
      return limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    },
  },
};
</script>

<style scoped>
.plan-card {
  /* Free = amber, Premium = blue, Business = purple (matches avatar shield) */
  --plan-border: #fcd9a0;
  --plan-bg: #fff8ec;
  --plan-text: #92400e;
  --plan-track: #fde7c3;
  --plan-accent: #f5a623;
  --plan-accent-hover: #e0951a;
  padding: 10px 12px;
  border: 1px solid var(--plan-border);
  background: var(--plan-bg);
  border-radius: 10px;
  box-sizing: border-box;
}
.plan-card--premium {
  --plan-border: #c7d7fb;
  --plan-bg: #f0f5ff;
  --plan-text: #1e3a8a;
  --plan-track: #dbe6fd;
  --plan-accent: #3467d5;
  --plan-accent-hover: #2952b3;
}
.plan-card--business {
  --plan-border: #d9cffb;
  --plan-bg: #f5f2ff;
  --plan-text: #4c1d95;
  --plan-track: #e6defd;
  --plan-accent: #6d4ae8;
  --plan-accent-hover: #5a3bd1;
}
.plan-card__label {
  display: block;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--plan-text);
  opacity: 0.75;
}
.plan-card__row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 2px 8px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--plan-text);
}
.plan-card__row > span {
  white-space: nowrap;
}
.plan-card__row--sub {
  font-weight: 500;
  font-size: 12px;
}
.plan-card__name {
  font-weight: 700;
}
.plan-card__meter {
  height: 6px;
  border-radius: 4px;
  background: var(--plan-track);
  margin: 8px 0 6px;
  overflow: hidden;
}
.plan-card__meter i {
  display: block;
  height: 100%;
  background: var(--plan-accent);
  border-radius: 4px;
  transition: width 0.3s;
}
.plan-card__note {
  margin: 0 0 6px;
  font-size: 12px;
  color: var(--plan-text);
}
.plan-card__note--warn {
  font-weight: 600;
  color: #b45309;
}
</style>
